'use client';

import { apiRequest } from './client';
import { ADMIN_TOKEN_KEY, API_BASE_URL } from './config';
import type { Employee } from './services';
import type { CatalogCustomer } from './services';

export type WorkAttachment = {
  id: string;
  fileName: string;
  fileUrl: string;
  uploadedBy?: string | null;
  createdAt?: string;
};

export type WorkProject = {
  id: string;
  clientId?: string | null;
  name: string;
  code: string;
  description: string;
  ownerId?: string | null;
  startDate: string;
  dueDate: string;
  status: string;
  priority: string;
  estimatedHours: number;
  loggedHours: number;
  remainingHours: number;
  loggedLabel: string;
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  overdueTasks: number;
  progress: number;
  tags: string[];
  client?: CatalogCustomer;
  owner?: Employee;
  members?: Array<{
    id: string;
    employeeId: string;
    role: string;
    employee?: Employee;
  }>;
  attachments?: WorkAttachment[];
};

export type WorkTask = {
  id: string;
  projectId: string;
  parentId?: string | null;
  title: string;
  description: string;
  priority: string;
  status: string;
  assigneeId?: string | null;
  estimatedHours: number;
  loggedSeconds: number;
  loggedHours: number;
  loggedLabel: string;
  varianceHours: number;
  progress: number;
  dueDate: string;
  overdue: boolean;
  tags: string[];
  project?: { id: string; name: string; code: string };
  assignee?: Employee;
  attachments?: WorkAttachment[];
  activities?: Array<{
    id: string;
    action: string;
    fieldName?: string;
    oldValue?: string;
    newValue?: string;
    remarks?: string;
    createdAt: string;
  }>;
};

export type ActiveTimer = {
  id: string;
  employeeId: string;
  projectId: string;
  taskId: string;
  startedAt: string;
  pausedAt?: string | null;
  resumedAt?: string | null;
  accumulatedSeconds: number;
  durationSeconds: number;
  durationLabel: string;
  status: 'running' | 'paused';
  serverNow: string;
  project?: { id: string; name: string; code: string } | null;
  task?: { id: string; title: string } | null;
  conflict?: boolean;
};

export type TimeEntry = {
  id: string;
  employeeId: string;
  projectId: string;
  taskId: string;
  workDate: string;
  startAt?: string | null;
  endAt?: string | null;
  durationSeconds: number;
  durationLabel: string;
  description: string;
  entryType: 'timer' | 'manual';
  status: string;
  isBillable: boolean;
  employee?: Employee;
  project?: { id: string; name: string; code: string };
  task?: { id: string; title: string };
};

export type Timesheet = {
  id: string;
  employeeId: string;
  periodStart: string;
  periodEnd: string;
  totalSeconds: number;
  totalLabel: string;
  timerSeconds: number;
  manualSeconds: number;
  timerLabel: string;
  manualLabel: string;
  status: string;
  submittedAt?: string | null;
  remarks: string;
  employee?: Employee;
  entries?: TimeEntry[];
};

export type AppNotification = {
  id: string;
  type: string;
  title: string;
  message: string;
  entityType?: string | null;
  entityId?: string | null;
  readAt?: string | null;
  createdAt: string;
};

function qs(params?: Record<string, string | undefined>) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v) search.set(k, v);
  });
  const suffix = search.toString();
  return suffix ? `?${suffix}` : '';
}

export const projectService = {
  getAll(params?: Record<string, string | undefined>) {
    return apiRequest<WorkProject[]>(`/admin/projects${qs(params)}`, { auth: true });
  },
  getOne(id: string) {
    return apiRequest<WorkProject>(`/admin/projects/${id}`, { auth: true });
  },
  create(payload: Record<string, unknown>) {
    return apiRequest<WorkProject>('/admin/projects', {
      method: 'POST',
      auth: true,
      bodyPayload: payload,
    });
  },
  update(id: string, payload: Record<string, unknown>) {
    return apiRequest<WorkProject>(`/admin/projects/${id}`, {
      method: 'PUT',
      auth: true,
      bodyPayload: payload,
    });
  },
  remove(id: string) {
    return apiRequest(`/admin/projects/${id}`, { method: 'DELETE', auth: true });
  },
};

export const taskApi = {
  getAll(params?: Record<string, string | undefined>) {
    return apiRequest<WorkTask[]>(`/admin/tasks${qs(params)}`, { auth: true });
  },
  getOne(id: string) {
    return apiRequest<WorkTask>(`/admin/tasks/${id}`, { auth: true });
  },
  create(payload: Record<string, unknown>) {
    return apiRequest<WorkTask>('/admin/tasks', {
      method: 'POST',
      auth: true,
      bodyPayload: payload,
    });
  },
  update(id: string, payload: Record<string, unknown>) {
    return apiRequest<WorkTask>(`/admin/tasks/${id}`, {
      method: 'PUT',
      auth: true,
      bodyPayload: payload,
    });
  },
  remove(id: string) {
    return apiRequest(`/admin/tasks/${id}`, { method: 'DELETE', auth: true });
  },
};

export const timerApi = {
  getActive() {
    return apiRequest<ActiveTimer | null>('/admin/time-tracking/active', { auth: true });
  },
  summary() {
    return apiRequest<{
      todaySeconds: number;
      weekSeconds: number;
      monthSeconds: number;
      todayLabel: string;
      weekLabel: string;
      monthLabel: string;
      todayEntries: TimeEntry[];
      recentEntries: TimeEntry[];
      myTasks: Array<{
        id: string;
        title: string;
        status: string;
        dueDate: string;
        project?: { id: string; name: string; code: string } | null;
      }>;
    }>('/admin/time-tracking/summary', { auth: true });
  },
  start(payload: { projectId: string; taskId?: string; taskTitle?: string }) {
    return apiRequest<ActiveTimer | { conflict: true; activeTimer: ActiveTimer }>(
      '/admin/time-tracking/start',
      { method: 'POST', auth: true, bodyPayload: payload }
    );
  },
  pause() {
    return apiRequest<ActiveTimer>('/admin/time-tracking/pause', {
      method: 'POST',
      auth: true,
      bodyPayload: {},
    });
  },
  resume() {
    return apiRequest<ActiveTimer>('/admin/time-tracking/resume', {
      method: 'POST',
      auth: true,
      bodyPayload: {},
    });
  },
  stop(payload?: string | { description?: string; isBillable?: boolean }) {
    const body = typeof payload === 'string' ? { description: payload } : payload || {};
    return apiRequest<{
      stopped: boolean;
      continued?: boolean;
      durationSeconds: number;
      durationLabel: string;
    }>('/admin/time-tracking/stop', { method: 'POST', auth: true, bodyPayload: body });
  },
};

export const timeEntryApi = {
  getAll(params?: Record<string, string | undefined>) {
    return apiRequest<TimeEntry[]>(`/admin/time-entries${qs(params)}`, { auth: true });
  },
  create(payload: Record<string, unknown>) {
    return apiRequest<TimeEntry>('/admin/time-entries', {
      method: 'POST',
      auth: true,
      bodyPayload: payload,
    });
  },
  update(id: string, payload: Record<string, unknown>) {
    return apiRequest<TimeEntry>(`/admin/time-entries/${id}`, {
      method: 'PUT',
      auth: true,
      bodyPayload: payload,
    });
  },
  remove(id: string) {
    return apiRequest(`/admin/time-entries/${id}`, { method: 'DELETE', auth: true });
  },
};

export const timesheetApi = {
  getAll(params?: Record<string, string | undefined>) {
    return apiRequest<Timesheet[]>(`/admin/timesheets${qs(params)}`, { auth: true });
  },
  getOne(id: string) {
    return apiRequest<Timesheet>(`/admin/timesheets/${id}`, { auth: true });
  },
  submit(workDate: string) {
    return apiRequest<Timesheet>('/admin/timesheets/submit', {
      method: 'POST',
      auth: true,
      bodyPayload: { workDate },
    });
  },
  approve(id: string) {
    return apiRequest<Timesheet>(`/admin/timesheets/${id}/approve`, {
      method: 'POST',
      auth: true,
      bodyPayload: {},
    });
  },
  reject(id: string, remarks: string) {
    return apiRequest<Timesheet>(`/admin/timesheets/${id}/reject`, {
      method: 'POST',
      auth: true,
      bodyPayload: { remarks },
    });
  },
  correction(id: string, remarks: string) {
    return apiRequest<Timesheet>(`/admin/timesheets/${id}/correction`, {
      method: 'POST',
      auth: true,
      bodyPayload: { remarks },
    });
  },
};

export const notificationApi = {
  getAll() {
    return apiRequest<{ items: AppNotification[]; unread: number }>('/admin/notifications', {
      auth: true,
    });
  },
  markRead(id: string) {
    return apiRequest(`/admin/notifications/${id}/read`, {
      method: 'POST',
      auth: true,
      bodyPayload: {},
    });
  },
  markAllRead() {
    return apiRequest('/admin/notifications/read-all', {
      method: 'POST',
      auth: true,
      bodyPayload: {},
    });
  },
};

export type TimeDashboard = {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  totalDevelopers: number;
  totalClients: number;
  pendingTasks: number;
  completedTasks: number;
  overdueTasks: number;
  pendingTimesheets: number;
  approvedTimesheets: number;
  rejectedTimesheets: number;
  totalHours: number;
  todayHours: number;
  weeklyHours: number;
  monthlyHours: number;
  todayLabel: string;
  weekLabel: string;
  monthLabel: string;
  totalLabel: string;
};

export const timeReportApi = {
  dashboard() {
    return apiRequest<TimeDashboard>('/admin/reports/time/dashboard', { auth: true });
  },
  utilization() {
    return apiRequest<
      Array<{
        employee: Employee;
        todaySeconds: number;
        weekSeconds: number;
        monthSeconds: number;
        todayLabel: string;
        weekLabel: string;
        monthLabel: string;
      }>
    >('/admin/reports/time/utilization', { auth: true });
  },
  projectHours(params?: Record<string, string | undefined>) {
    return apiRequest<{ rows: any[] }>(`/admin/reports/project-hours${qs(params)}`, {
      auth: true,
    });
  },
  employeeHours(params?: Record<string, string | undefined>) {
    return apiRequest<{ rows: any[] }>(`/admin/reports/employee-hours${qs(params)}`, {
      auth: true,
    });
  },
  taskHours(params?: Record<string, string | undefined>) {
    return apiRequest<{ rows: any[] }>(`/admin/reports/task-hours${qs(params)}`, {
      auth: true,
    });
  },
  clientHours(params?: Record<string, string | undefined>) {
    return apiRequest<{ rows: any[] }>(`/admin/reports/client-hours${qs(params)}`, {
      auth: true,
    });
  },
  async export(params: { format: string; type: string; dateFrom?: string; dateTo?: string }) {
    const token = typeof window !== 'undefined' ? localStorage.getItem(ADMIN_TOKEN_KEY) : null;
    const search = new URLSearchParams(params as Record<string, string>);
    const response = await fetch(`${API_BASE_URL}/admin/reports/time/export?${search}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-report.${params.format === 'excel' ? 'xls' : params.format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
